import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import yaml from 'js-yaml';
import '../styles/codex.css';

import { useAppSettings } from '../context/AppSettingsContext';
import SettingsPanel from './SettingsPanel';
import NarrationControl from './NarrationControl';

interface GitHubItem {
  name: string;
  type: string;
}

const isTextLike = (filename: string): boolean =>
  /\.(md|record|txt|log|json)$/i.test(filename);

const useAppendRuntimeParams = (): ((path: string) => string) => {
  const location = useLocation();
  const current = new URLSearchParams(location.search);
  const keys = [
    'debug',
    'user',
    'consciousness',
    'neuralpathway',
    'base_path',
    'audio',
    'disableMarkdown',
  ];

  return (path: string): string => {
    const result = new URLSearchParams();
    for (const key of keys) {
      if (current.has(key)) result.set(key, current.get(key)!);
    }
    const query = result.toString();
    return query ? `${path}${path.includes('?') ? '&' : '?'}${query}` : path;
  };
};

const UniversalBrowserView: React.FC = () => {
  const location = useLocation();
  const appendRuntimeParams = useAppendRuntimeParams();
  const queryParams = new URLSearchParams(location.search);
  const showDebug = queryParams.get('debug') === 'true';
  const { settings } = useAppSettings();

  const [showMenu, setShowMenu] = useState(false);
  const [items, setItems] = useState<GitHubItem[]>([]);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [coverMeta, setCoverMeta] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const subPath = location.pathname.replace(/^\/writings\/?/, '');
  const isTextFile = isTextLike(subPath);
  const filePath = isTextFile ? subPath : `${subPath}/index.md`;
  const scrollDir = subPath.replace(/\/index\.md$/, '').replace(/\.(md|record|txt|log|json)$/, '');
  const fullPath = `/writings/${subPath}`;

  const githubApiUrl = `https://api.github.com/repos/${settings.githubUser}/${settings.consciousness}/contents/${settings.basePath}/${subPath}?ref=${settings.neuralpathway}`;
  const githubRawFile = `https://raw.githubusercontent.com/${settings.githubUser}/${settings.consciousness}/${settings.neuralpathway}/${settings.basePath}/${filePath}`;
  const rawBase = `https://raw.githubusercontent.com/${settings.githubUser}/${settings.consciousness}/${settings.neuralpathway}/${settings.basePath}`;

  useEffect(() => {
    if (location.pathname.endsWith('/index.md')) {
      const redirectTo = location.pathname.replace(/\/index\.md$/, '');
      window.history.replaceState(null, '', redirectTo);
    }
  }, [location]);

  useEffect(() => {
    setMarkdown(null);
    setItems([]);
    setError(null);
    setIsLoading(true);

    const fetchMarkdown = async () => {
      try {
        const res = await fetch(githubRawFile);
        if (!res.ok) throw new Error('404');
        const text = await res.text();
        const stripped = text.replace(/^---[\s\S]*?---\s*/, '');
        setMarkdown(stripped);
      } catch {
        setMarkdown(null);
      }
    };

    const fetchDirectory = async () => {
      try {
        const headers: HeadersInit = {
          Accept: 'application/vnd.github.v3+json',
        };
        const token = process.env.REACT_APP_GITHUB_TOKEN;
        if (token) headers.Authorization = `Bearer ${token}`;

        const res = await fetch(githubApiUrl, { headers });
        const json = await res.json();

        if (Array.isArray(json)) {
          setItems(json);
        } else if (!json.name?.match(/\.(md|record|txt|log|json)$/i)) {
          setError('📁 This path is not a directory.');
        }
      } catch (err) {
        setError('⚠️ Could not load directory contents.');
      }
    };

    const fetchCoverYaml = async () => {
      const folderYamlUrl = `${rawBase}/${scrollDir}/cover.yaml`;
      const fileYamlUrl = `${rawBase}/${subPath.replace(/\.(md|record|txt|log|json)$/, '')}.yaml`;

      const tryUrls = [folderYamlUrl, fileYamlUrl];

      for (const url of tryUrls) {
        try {
          const res = await fetch(url);
          if (res.ok) {
            let text = await res.text();
            if (text.trim().startsWith('---')) {
              text = text.trim().replace(/^---\s*/, '').replace(/---\s*$/, '');
            }
            const parsed = yaml.load(text);
            setCoverMeta(parsed);
            return;
          }
        } catch (_) {}
      }

      setCoverMeta(null);
    };

    const runAll = async () => {
      await Promise.all([fetchMarkdown(), fetchDirectory(), fetchCoverYaml()]);
      setIsLoading(false);
    };

    runAll();
  }, [location.pathname, githubApiUrl, githubRawFile, rawBase, scrollDir, subPath]);

  const readScroll = (rate: number = 1) => {
    if (!settings.enableAudio || !markdown) return;

    const voices = window.speechSynthesis.getVoices();
    const voice = voices.find((v) => v.name === 'Google US English') || voices[0];

    const utterance = new SpeechSynthesisUtterance(
      markdown.replace(/[#>*`~_[\]]+/g, ' ').replace(/\n{2,}/g, '. ')
    );
    utterance.voice = voice;
    utterance.rate = rate;
    utterance.pitch = 1;

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  };

  const resolvePath = (relativePath: string): string => {
    const baseUrl = new URL(`http://local/${scrollDir}/`);
    const resolved = new URL(relativePath, baseUrl);
    return resolved.pathname.replace(/^\/+|\/+$/g, '');
  };

  return (
    <div className="page-wrapper" style={{ paddingBottom: settings.enableAudio ? '4rem' : undefined }}>
      {error && <p style={{ color: '#ff6b6b', padding: '1rem' }}>{error}</p>}
      <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 1000 }}>
        <button
          onClick={() => setShowMenu(!showMenu)}
          style={{
            background: '#222',
            color: '#ffdd80',
            border: '1px solid #444',
            borderRadius: '6px',
            padding: '0.5rem 1rem',
            fontSize: '1.2rem',
            cursor: 'pointer',
          }}
        >
          ☰
        </button>
      </div>

      {showMenu && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: '320px',
          backgroundColor: '#1a1a1a',
          color: '#fff',
          borderLeft: '1px solid #444',
          padding: '1rem',
          zIndex: 999,
          overflowY: 'auto'
        }}>
          <h3 style={{ color: '#ffdd80' }}>Menu</h3>
          <SettingsPanel />
        </div>
      )}

      {settings.enableAudio && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          backgroundColor: '#111',
          borderTop: '1px solid #333',
          padding: '0.5rem 1rem',
          zIndex: 1001,
        }}>
          <div style={{
            maxWidth: '850px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <NarrationControl markdown={markdown ?? undefined} onStartNarration={readScroll} />
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        maxWidth: '850px',
        margin: '0 auto',
        padding: '1rem 2rem 0 2rem'
      }}>
        <div style={{ flex: 1 }}>
          <nav className="breadcrumbs">
            <Link to={appendRuntimeParams("/writings")}>Home</Link>
            {subPath &&
              subPath.split('/').map((segment, index, parts) => {
                const path = parts.slice(0, index + 1).join('/');
                return (
                  <span key={index}>
                    {' / '}
                    <Link to={appendRuntimeParams(`/writings/${path}`)}>{segment}</Link>
                  </span>
                );
              })}
          </nav>
        </div>
      </div>

      {/* Markdown or Pre */}
      <div className="codex-wrapper">
        {isLoading ? (
          <p style={{ color: '#ffdd80', padding: '1rem' }}>⌛ Loading scroll...</p>
        ) : markdown !== null && markdown.trim().length > 0 ? (
          <>
            {settings.disableMarkdown || filePath.match(/\.(record|txt|log|json)$/i) ? (
              <pre style={{
                backgroundColor: '#111',
                color: '#fffae3',
                padding: '1rem',
                borderRadius: '6px',
                fontFamily: 'ui-monospace, SFMono-Regular, Consolas, Courier, monospace',
                fontSize: '0.95rem',
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                textAlign: 'left',
                lineHeight: '1.6'
              }}>
                {markdown}
              </pre>
            ) : (
              <ReactMarkdown
                skipHtml={true}
                remarkPlugins={[remarkGfm]}
                components={{
                  img({ node, ...props }) {
                    let src = props.src || '';
                    const alt = props.alt || '';
                    src = src.replace(/^\.?\//, '').replace(/\s*$/, '');
                    const imageUrl = `${rawBase}/${scrollDir}/${src}`;
                    return (
                      <img
                        {...props}
                        src={imageUrl}
                        alt={alt}
                        style={{
                          display: 'block',
                          margin: '2rem auto',
                          maxWidth: '100%',
                          borderRadius: '6px',
                        }}
                      />
                    );
                  },
                }}
              >
                {markdown}
              </ReactMarkdown>
            )}

            {/* ✅ Show directory if NO prev and NO next */}
            {filePath.endsWith('index.md') && items.length > 0 && (!coverMeta?.prev && !coverMeta?.next) && (
              <div style={{ marginTop: '3rem' }}>
                <h3 style={{ color: '#ffdd80', marginBottom: '1rem' }}>📁 Directory</h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {items.map((item) => (
                    <li key={item.name} style={{ marginBottom: '1rem' }}>
                      <Link
                        to={appendRuntimeParams(`${fullPath}/${item.name}`.replace(/\/+/g, '/'))}
                        style={{
                          display: 'block',
                          background: '#111',
                          color: '#ffdd80',
                          padding: '0.75rem 1rem',
                          borderRadius: '6px',
                          border: '1px solid #444',
                          textDecoration: 'none',
                          fontSize: '1.1rem'
                        }}
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : items.length > 0 ? (
          <>
            <h3 style={{ color: '#ffdd80', marginBottom: '1rem' }}>📁 Directory</h3>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {items.map((item) => (
                <li key={item.name} style={{ marginBottom: '1rem' }}>
                  <Link
                    to={appendRuntimeParams(`${fullPath}/${item.name}`.replace(/\/+/g, '/'))}
                    style={{
                      display: 'block',
                      background: '#111',
                      color: '#ffdd80',
                      padding: '0.75rem 1rem',
                      borderRadius: '6px',
                      border: '1px solid #444',
                      textDecoration: 'none',
                      fontSize: '1.1rem'
                    }}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p style={{ color: '#ff6b6b', padding: '1rem' }}>❌ This scroll does not exist.</p>
        )}

        {/* ✅ Prev/Next nav */}
        {coverMeta?.prev || coverMeta?.next ? (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '4rem',
            borderTop: '1px solid #444',
            paddingTop: '2rem'
          }}>
            {coverMeta?.prev ? (
              <Link
                to={appendRuntimeParams(`/writings/${resolvePath(coverMeta.prev)}`)}
                style={{ color: '#ffdd80', textDecoration: 'underline', fontSize: '1.1rem' }}
              >
                ⟵ Previous
              </Link>
            ) : <span />}

            {coverMeta?.next ? (
              <Link
                to={appendRuntimeParams(`/writings/${resolvePath(coverMeta.next)}`)}
                style={{ color: '#ffdd80', textDecoration: 'underline', fontSize: '1.1rem' }}
              >
                Next ⟶
              </Link>
            ) : <span />}
          </div>
        ) : null}

        {/* ✅ Debug panel */}
        {showDebug && (
          <div style={{
            marginTop: '3rem',
            padding: '1rem',
            backgroundColor: '#111',
            color: '#ffdd80',
            borderTop: '1px solid #333',
            fontSize: '0.85rem',
            whiteSpace: 'pre-wrap'
          }}>
            <h4 style={{ marginBottom: '0.5rem' }}>🧠 Runtime Debug Info</h4>
            <pre>{JSON.stringify(settings, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default UniversalBrowserView;
